import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    AlertCircleStrokeRounded,
    CheckmarkCircle02StrokeRounded,
    Delete02StrokeRounded,
    Download01StrokeRounded,
    File01StrokeRounded,
    Globe02StrokeRounded,
    SquareLock02StrokeRounded,
} from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, CircularProgress, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { DateTime } from '@/common/components'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { ATTACHMENT_MAX_PER_PARENT, ATTACHMENT_SCAN_STATE } from '@/common/utils/Constants/Constants'

import { formatBytes } from './formatBytes'

/**
 * Shared by the row and the panel's column header so the two stay aligned. Visibility and status
 * have fixed floors (the Staff / Customer toggle and "Checking file…" must never overlap); they are
 * pixels, not max-content, because each row is its own grid and content-sized tracks would drift
 * between rows.
 */
export const ATTACHMENT_ROW_COLUMNS = 'minmax(0, 4fr) minmax(0, 2fr) minmax(200px, 2fr) minmax(130px, 2fr) 96px'

/** Below this width the table scrolls sideways instead of squeezing the name and uploader columns. */
export const ATTACHMENT_TABLE_MIN_WIDTH = 900

const VISIBILITY = { staff: 'staff', customer: 'customer' }

/**
 * QuoteAttachmentRow - One attached document as a ledger row: name, uploaded, visibility,
 * status, actions. Scan state and visibility are independent: pending rows offer neither
 * download nor delete, failed rows make remove the action, and the visibility control works in
 * every state.
 *
 * @param {Object} props
 * @param {Object} props.attachment - DocumentDto row
 * @param {boolean} [props.busy] - Disables controls while a mutation for this row is in flight
 * @param {boolean} [props.isDestinationFull] - The other visibility has no free slots, so the row
 *   cannot switch to it
 * @param {string} [props.locale]
 * @param {Function} props.onDelete - (attachment) => void
 * @param {Function} props.onDownload - (attachment) => void
 * @param {Function} props.onToggleVisibility - (attachment, nextIsPublic) => void
 * @param {string} [props.uploaderName]
 */
const QuoteAttachmentRow = ({
    attachment,
    busy = false,
    isDestinationFull = false,
    locale = 'en-US',
    onDelete,
    onDownload,
    onToggleVisibility,
    uploaderName,
}) => {
    const { t } = useTranslation()

    const isPending = attachment.scanState === ATTACHMENT_SCAN_STATE.Pending
    const isFailed = attachment.scanState === ATTACHMENT_SCAN_STATE.Failed
    const isClean = attachment.scanState === ATTACHMENT_SCAN_STATE.Clean
    const canDownload = isClean && Boolean(attachment.downloadUri)

    const blockedReason = isDestinationFull
        ? t(attachment.isPublic ? 'All {{max}} staff slots are used.' : 'All {{max}} customer slots are used.', {
              max: ATTACHMENT_MAX_PER_PARENT,
          })
        : ''

    const handleVisibilityChange = (_event, value) => {
        // Exclusive group returns null when the selected side is pressed again.
        if (value === null) return
        onToggleVisibility(attachment, value === VISIBILITY.customer)
    }

    return (
        <Box
            aria-label={attachment.documentName}
            component="li"
            sx={{
                alignItems: 'center',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: ATTACHMENT_ROW_COLUMNS,
                listStyle: 'none',
                px: 1.5,
                py: 1.25,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => (isFailed ? alpha(theme.palette.error.main, 0.06) : 'transparent'),
                borderRadius: isFailed ? 1 : 0,
            }}
        >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, minWidth: 0 }}>
                <Box
                    component="span"
                    sx={{ color: isFailed ? 'error.main' : 'action.active', display: 'inline-flex' }}
                >
                    <HugeiconsIcon
                        icon={isFailed ? AlertCircleStrokeRounded : File01StrokeRounded}
                        size={22}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        fontWeight={500}
                        title={attachment.documentName}
                        variant="body2"
                        noWrap
                    >
                        {attachment.documentName}
                    </Typography>
                    <Typography
                        color={isFailed ? 'error' : 'text.secondary'}
                        variant="caption"
                        noWrap
                    >
                        {isFailed ? t('Could not be accepted. Remove it to free the slot.') : null}
                        {isPending ? t('Still being checked. Refresh the page to see the result.') : null}
                        {isClean ? formatBytes(attachment.sizeBytes) : null}
                    </Typography>
                </Box>
            </Box>

            <Typography
                color="text.secondary"
                variant="body2"
            >
                {uploaderName ? (
                    <>
                        {uploaderName}
                        <br />
                    </>
                ) : null}
                <DateTime
                    format="DD-MMM-YYYY"
                    locale={locale}
                >
                    {attachment.createDateTime}
                </DateTime>
            </Typography>

            <ToggleButtonGroup
                disabled={busy}
                size="small"
                value={attachment.isPublic ? VISIBILITY.customer : VISIBILITY.staff}
                exclusive
                onChange={handleVisibilityChange}
            >
                <TbxTooltip title={attachment.isPublic ? blockedReason : ''}>
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex' }}
                    >
                        <ToggleButton
                            aria-label={t('Staff only')}
                            disabled={busy || (attachment.isPublic && isDestinationFull)}
                            sx={{
                                gap: 0.75,
                                px: 1.25,
                                '&.Mui-selected': { bgcolor: 'text.primary', color: 'common.white' },
                            }}
                            value={VISIBILITY.staff}
                        >
                            <HugeiconsIcon
                                icon={SquareLock02StrokeRounded}
                                size={16}
                            />
                            {t('Staff')}
                        </ToggleButton>
                    </Box>
                </TbxTooltip>
                <TbxTooltip title={attachment.isPublic ? '' : blockedReason}>
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex' }}
                    >
                        <ToggleButton
                            aria-label={t('Visible to customer')}
                            disabled={busy || (!attachment.isPublic && isDestinationFull)}
                            sx={{
                                gap: 0.75,
                                px: 1.25,
                                '&.Mui-selected': { bgcolor: 'primary.main', color: 'common.white' },
                            }}
                            value={VISIBILITY.customer}
                        >
                            <HugeiconsIcon
                                icon={Globe02StrokeRounded}
                                size={16}
                            />
                            {t('Customer')}
                        </ToggleButton>
                    </Box>
                </TbxTooltip>
            </ToggleButtonGroup>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                {isPending ? (
                    <>
                        <CircularProgress
                            color="secondary"
                            size={18}
                            thickness={5}
                            disableShrink
                        />
                        <Typography
                            color="secondary"
                            variant="body2"
                        >
                            {t('Checking file…')}
                        </Typography>
                    </>
                ) : null}
                {isClean ? (
                    <>
                        <Box
                            component="span"
                            sx={{ color: 'success.main', display: 'inline-flex' }}
                        >
                            <HugeiconsIcon
                                icon={CheckmarkCircle02StrokeRounded}
                                size={18}
                            />
                        </Box>
                        <Typography
                            color="success.main"
                            variant="body2"
                        >
                            {t('Ready')}
                        </Typography>
                    </>
                ) : null}
                {isFailed ? (
                    <Typography
                        color="error"
                        fontWeight={500}
                        variant="body2"
                    >
                        {t('Not accepted')}
                    </Typography>
                ) : null}
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                {canDownload ? (
                    // The span keeps the tooltip working while the button is disabled.
                    <TbxTooltip title={t('Download')}>
                        <span>
                            <IconButton
                                aria-label={t('Download attachment')}
                                disabled={busy}
                                size="small"
                                onClick={() => onDownload(attachment)}
                            >
                                <HugeiconsIcon
                                    icon={Download01StrokeRounded}
                                    size={20}
                                />
                            </IconButton>
                        </span>
                    </TbxTooltip>
                ) : null}
                {attachment.canDelete && isFailed ? (
                    <Button
                        color="error"
                        disabled={busy}
                        size="small"
                        variant="outlined"
                        onClick={() => onDelete(attachment)}
                    >
                        {t('Remove')}
                    </Button>
                ) : null}
                {attachment.canDelete && !isFailed ? (
                    <TbxTooltip title={t('Delete')}>
                        <span>
                            <IconButton
                                aria-label={t('Delete attachment')}
                                disabled={busy}
                                size="small"
                                onClick={() => onDelete(attachment)}
                            >
                                <HugeiconsIcon
                                    icon={Delete02StrokeRounded}
                                    size={20}
                                />
                            </IconButton>
                        </span>
                    </TbxTooltip>
                ) : null}
            </Box>
        </Box>
    )
}

QuoteAttachmentRow.propTypes = {
    attachment: PropTypes.shape({
        documentId: PropTypes.string.isRequired,
        documentName: PropTypes.string.isRequired,
        canDelete: PropTypes.bool,
        createDateTime: PropTypes.string,
        createdBy: PropTypes.string,
        downloadUri: PropTypes.string,
        isPublic: PropTypes.bool,
        scanState: PropTypes.number,
        sizeBytes: PropTypes.number,
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    onToggleVisibility: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    isDestinationFull: PropTypes.bool,
    locale: PropTypes.string,
    uploaderName: PropTypes.string,
}

export default QuoteAttachmentRow
